import type { GenerateMetadataFunction } from 'expo-router/server';
import { staticArticleMetadata } from '../../../lib/i18n/authoredPageMetadata';
import { View, Text, StyleSheet, ScrollView, Platform, TouchableOpacity, Image, Linking, ActivityIndicator } from 'react-native';
import { Stack, Link, useLocalSearchParams } from 'expo-router';
import { useState, useEffect } from 'react';
import { FontAwesome6 } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import DOMPurify from 'dompurify';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import Header from '../../../components/Header';
import Footer from '../../../components/Footer';
import MetaTags from '../../../components/MetaTags';
import NewsletterSubscribePrompt from '../../../components/newsletter/NewsletterSubscribePrompt';
import StaticJsonScript from '../../../components/StaticJsonScript';
import {
    ArticleHtmlContent,
    injectArticleContentStyles,
} from '../../../components/article/ArticleHtmlContent';
import { formatArticleDate } from '../../../lib/articleDate';
import colors from '../../../lib/colors';
import { useAppLocale } from '../../../lib/i18n/AppI18nProvider';
import {
    isProductionPublicBuild,
    publicCanonicalUrl,
} from '../../../lib/i18n/publicWeb';
import {
    getStaticArticleContent,
    fetchStaticArticle,
    getStaticArticleIndex,
    readEmbeddedJson,
    type StaticArticleContent,
    type StaticArticleMeta,
} from '../../../lib/staticNewsContent';

const NEWS_ARTICLE_SCRIPT_ID = 'pz-news-article-ssg';
type NewsArticleError = 'index-load' | 'not-found' | 'content-load';

export async function generateStaticParams(): Promise<{ slug: string }[]> {
    return getStaticArticleIndex('news').map((article) => ({ slug: article.slug }));
}

function initialArticleForSlug(slug: string | undefined): StaticArticleContent | null {
    return (
        readEmbeddedJson<StaticArticleContent>(NEWS_ARTICLE_SCRIPT_ID) ||
        getStaticArticleContent('news', slug)
    );
}

export default function NewsArticlePage() {
    const { i18n } = useLingui();
    const { formatLocale, messageLocale } = useAppLocale();
    const { slug } = useLocalSearchParams<{ slug: string }>();
    const initialArticle = initialArticleForSlug(slug);
    const [article, setArticle] = useState<StaticArticleMeta | null>(initialArticle);
    const [sanitizedHtml, setSanitizedHtml] = useState<string>(initialArticle?.html || '');
    const [loading, setLoading] = useState(!initialArticle);
    const [error, setError] = useState<NewsArticleError | null>(null);

    const canonicalPathname = `/news/${slug}`;
    const sharePageUrl = isProductionPublicBuild()
        ? publicCanonicalUrl(
            'news-article',
            messageLocale,
            canonicalPathname,
            { allowTestLocale: messageLocale === 'en-XA' }
        )
        : Platform.OS === 'web' && typeof window !== 'undefined'
            ? window.location.href
            : null;
    const socialPreviewImage = article?.twitterImage || article?.socialImage || article?.featuredImage || undefined;

    useEffect(() => {
        if (!slug) return;
        if (article?.slug === slug && sanitizedHtml) return;

        const loadArticle = async () => {
            let failure: NewsArticleError = 'index-load';
            try {
                const loaded = await fetchStaticArticle('news', slug, messageLocale);
                if (!loaded) {
                    failure = 'not-found';
                    throw new Error(`Article not found: ${slug}`);
                }
                setArticle(loaded);
                const html = loaded.html;

                // SECURITY: Sanitize HTML with DOMPurify before storing/rendering
                // Even though content comes from our own markdown files, we sanitize as defense-in-depth
                const cleanHtml = DOMPurify.sanitize(html, {
                    ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
                                   'blockquote', 'strong', 'em', 'a', 'code', 'pre', 'br',
                                   'table', 'tr', 'td', 'th', 'tbody', 'thead', 'span'],
                    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'width',
                                   'cellpadding', 'cellspacing', 'border'],
                });
                setSanitizedHtml(cleanHtml);

                setLoading(false);
            } catch (err: any) {
                console.error('Error loading article:', err);
                setError(failure);
                setLoading(false);
            }
        };

        loadArticle();
    }, [slug, article?.slug, sanitizedHtml]);

    const errorMessage = error === 'index-load'
        ? i18n._(msg({
            id: 'news.article.indexLoadFailure',
            message: 'Failed to load article index',
        }))
        : error === 'content-load'
            ? i18n._(msg({
                id: 'news.article.contentLoadFailure',
                message: 'Failed to load article content',
            }))
            : i18n._(msg({
                id: 'news.article.notFound',
                message: 'Article not found',
            }));

    const handleShare = (platform: 'twitter' | 'linkedin' | 'facebook') => {
        if (!article) return;
        const text = encodeURIComponent(article.title);
        if (!sharePageUrl) return;
        const url = encodeURIComponent(sharePageUrl);

        const shareUrls = {
            twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
            linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${text}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
        };

        Linking.openURL(shareUrls[platform]);
    };

    // Loading state
    if (loading) {
        return (
            <>
                <Stack.Screen options={{ headerShown: false }} />
                <View style={styles.page}>
                    <Header />
                    <View style={styles.loadingContainer}>
                        <ActivityIndicator size="large" color={colors.primaryGreen} />
                    </View>
                </View>
            </>
        );
    }

    // Error state
    if (error || !article) {
        return (
            <>
                <Stack.Screen options={{ headerShown: false }} />
                <View style={styles.page}>
                    <Header />
                    <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
                        <View style={styles.container}>
                            <Link href="/news" asChild>
                                <TouchableOpacity style={styles.backLink}>
                                    <Text style={styles.backLinkText}>
                                        <Trans id="news.article.backToNews">← Back to News</Trans>
                                    </Text>
                                </TouchableOpacity>
                            </Link>
                            <View style={styles.errorContainer}>
                                <Text style={styles.errorText}>
                                    {errorMessage}
                                </Text>
                            </View>
                        </View>
                        <Footer />
                    </ScrollView>
                </View>
            </>
        );
    }

    return (
        <>
            <Stack.Screen options={{ headerShown: false }} />
            <StaticJsonScript
                id={NEWS_ARTICLE_SCRIPT_ID}
                value={article && sanitizedHtml ? { ...article, html: sanitizedHtml } : initialArticle}
            />
            <MetaTags
                surfaceId="news-article"
                contentLocale={article.contentLocale}
                canonicalPathname={canonicalPathname}
                title={article.title}
                description={article.excerpt}
                image={socialPreviewImage}
                type="article"
            />
            <View style={styles.page}>
                <Header />
                <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
                    {/* Article Hero — title and meta on gradient background */}
                    <LinearGradient
                        colors={["#ffffff", "#fbfcff", "#f6f7fb"]}
                        start={{ x: 0.5, y: 0 }}
                        end={{ x: 0.5, y: 1 }}
                        style={styles.heroGradient}
                    >
                        <View style={styles.heroContainer}>
                            <Link href="/news" asChild>
                                <TouchableOpacity style={styles.backLink}>
                                    <Text style={styles.backLinkText}>
                                        <Trans id="news.article.backToNews">← Back to News</Trans>
                                    </Text>
                                </TouchableOpacity>
                            </Link>
                            <Text
                                style={styles.articleTitle}
                                accessibilityRole="header"
                                {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 1 })}
                            >
                                {article.title}
                            </Text>
                            <View style={styles.articleMeta}>
                                <Text style={styles.articleDate}>{formatArticleDate(article.publishedDate, formatLocale)}</Text>
                                {article.author && (
                                    <>
                                        <Text style={styles.metaSeparator}>·</Text>
                                        <Text style={styles.articleAuthor}>{i18n._({ ...msg({
                                            id: 'article.byAuthor',
                                            message: 'by {author}',
                                        }), values: { author: article.author } })}</Text>
                                    </>
                                )}
                            </View>
                            <NewsletterSubscribePrompt style={styles.subscribePrompt} />
                        </View>
                    </LinearGradient>

                    {/* Article Body */}
                    <View style={styles.container}>
                        {/* Featured Image */}
                        {article.featuredImage && (
                            <Image
                                source={{ uri: article.featuredImage }}
                                defaultSource={Platform.OS === 'web' ? { uri: article.featuredImage } : undefined}
                                style={styles.featuredImage}
                                resizeMode="contain"
                                accessibilityLabel={article.title}
                            />
                        )}

                        {/* Content Card */}
                        <View style={styles.contentCard} {...(Platform.OS === 'web' && { lang: article.contentLocale })}>
                            <ArticleHtmlContent html={sanitizedHtml} className="news-article-content" />
                        </View>

                        {/* Product Callout */}
                        <View style={styles.paperzillaCallout}>
                            <Text style={styles.paperzillaCalloutTitle}>
                                <Trans id="news.article.whatIsPaperzilla">What is Paperzilla?</Trans>
                            </Text>
                            <Text style={styles.paperzillaCalloutText}>
                                <Trans id="news.article.productDescription">Paperzilla watches new preprints and PubMed-indexed records and ranks what matches your research. Get a short morning brief of the papers worth reading in Claude, ChatGPT or another AI assistant, or follow them in the app, by email digest or via RSS/Atom.</Trans>
                            </Text>
                            <TouchableOpacity
                                style={styles.paperzillaCalloutCta}
                                onPress={() => Linking.openURL('https://paperzilla.ai?utm_source=news_article&utm_medium=article&utm_content=try_for_free')}
                            >
                                <Text style={styles.paperzillaCalloutCtaText}>
                                    <Trans id="news.article.tryForFree">Try it for free</Trans>
                                </Text>
                            </TouchableOpacity>
                        </View>

                        {/* Share Section */}
                        <View style={styles.shareSection}>
                            <Text style={styles.shareLabel}>
                                <Trans id="news.article.share">Share this article</Trans>
                            </Text>
                            <View style={styles.shareButtons}>
                                <TouchableOpacity
                                    style={styles.shareButton}
                                    onPress={() => handleShare('twitter')}
                                    accessibilityLabel={i18n._(msg({ id: 'article.shareOnX', message: 'Share on X' }))}
                                >
                                    <FontAwesome6 name="square-x-twitter" size={20} color="#6b7280" />
                                    <Text style={styles.shareButtonText}>
                                        <Trans id="article.twitter">Twitter</Trans>
                                    </Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={styles.shareButton}
                                    onPress={() => handleShare('linkedin')}
                                    accessibilityLabel={i18n._(msg({ id: 'article.shareOnLinkedIn', message: 'Share on LinkedIn' }))}
                                >
                                    <FontAwesome6 name="linkedin" size={20} color="#6b7280" />
                                    <Text style={styles.shareButtonText}>
                                        <Trans id="article.linkedin">LinkedIn</Trans>
                                    </Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={styles.shareButton}
                                    onPress={() => handleShare('facebook')}
                                    accessibilityLabel={i18n._(msg({ id: 'article.shareOnFacebook', message: 'Share on Facebook' }))}
                                >
                                    <FontAwesome6 name="facebook" size={20} color="#6b7280" />
                                    <Text style={styles.shareButtonText}>
                                        <Trans id="article.facebook">Facebook</Trans>
                                    </Text>
                                </TouchableOpacity>
                            </View>
                        </View>

                        {/* Back to news link */}
                        <Link href="/news" asChild>
                            <TouchableOpacity style={styles.bottomBackLink}>
                                <Text style={styles.backLinkText}>
                                    <Trans id="news.article.backToAll">← Back to all news</Trans>
                                </Text>
                            </TouchableOpacity>
                        </Link>
                    </View>

                    <Footer />
                </ScrollView>
            </View>
        </>
    );
}

injectArticleContentStyles('news-article-styles');

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    heroGradient: {
        width: '100%',
    },
    heroContainer: {
        width: '100%',
        maxWidth: 900,
        alignSelf: 'center',
        paddingHorizontal: 24,
        paddingTop: 32,
        paddingBottom: 40,
    },
    subscribePrompt: {
        marginTop: 20,
        width: '100%',
    },
    container: {
        width: '100%',
        maxWidth: 900,
        alignSelf: 'center',
        paddingHorizontal: 24,
        paddingTop: 32,
        paddingBottom: 40,
    },
    errorContainer: {
        backgroundColor: '#fef2f2',
        borderWidth: 1,
        borderColor: '#fecaca',
        borderRadius: 8,
        padding: 20,
        marginTop: 20,
    },
    errorText: {
        color: '#dc2626',
        fontSize: 16,
        textAlign: 'center',
    },
    backLink: {
        marginBottom: 24,
    },
    backLinkText: {
        fontSize: 16,
        color: colors.primaryGreen,
        fontWeight: '500',
    },
    articleTitle: {
        fontSize: 36,
        fontWeight: 'bold',
        color: colors.primaryGreenDark,
        lineHeight: 46,
        marginBottom: 16,
    },
    articleMeta: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    articleDate: {
        fontSize: 16,
        color: '#6b7280',
    },
    metaSeparator: {
        fontSize: 16,
        color: '#9ca3af',
        marginHorizontal: 10,
    },
    articleAuthor: {
        fontSize: 16,
        color: '#6b7280',
    },
    featuredImage: {
        width: '100%',
        height: 300,
        borderRadius: 12,
        marginBottom: 24,
        backgroundColor: '#f3f4f6',
    },
    contentCard: {
        backgroundColor: 'white',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.border,
        paddingHorizontal: 32,
        paddingVertical: 32,
        marginBottom: 24,
    },
    paperzillaCallout: {
        backgroundColor: '#eef7e8',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#d7e8cf',
        paddingHorizontal: 24,
        paddingVertical: 24,
        marginBottom: 24,
    },
    paperzillaCalloutTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: colors.primaryGreenDark,
        marginBottom: 10,
    },
    paperzillaCalloutText: {
        fontSize: 16,
        lineHeight: 27,
        color: '#374151',
    },
    paperzillaCalloutCta: {
        alignSelf: 'flex-start',
        marginTop: 16,
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
        backgroundColor: colors.primaryGreenDark,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    paperzillaCalloutCtaText: {
        color: 'white',
        fontSize: 14,
        fontWeight: '600',
    },
    shareSection: {
        backgroundColor: 'white',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.border,
        paddingHorizontal: 24,
        paddingVertical: 24,
        marginBottom: 24,
    },
    shareLabel: {
        fontSize: 16,
        fontWeight: '500',
        color: '#374151',
        marginBottom: 16,
    },
    shareButtons: {
        flexDirection: 'row',
        gap: 12,
    },
    shareButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#f3f4f6',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
        gap: 8,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'background-color 0.2s',
        }),
    },
    shareButtonText: {
        fontSize: 14,
        color: '#374151',
        fontWeight: '500',
    },
    bottomBackLink: {
        marginBottom: 40,
    },
});

export const generateMetadata: GenerateMetadataFunction = (_request, params) =>
    staticArticleMetadata('news', params.slug);
